import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Fire, Microphone } from '@phosphor-icons/react';
import { PiMagnifyingGlassBold } from 'react-icons/pi';
import VoiceSearchSheet from './VoiceSearchSheet';
import { vozSoportada } from '../../hooks/useVoiceSearch';
import ConfirmModal from '../shared/ConfirmModal';

const PALABRAS_ROTATIVAS = [
  { text: 'hamburguesa' },
  { text: 'combo' },
  { text: 'bebida' },
  { text: 'postre' },
  { text: 'antojo', hasFlame: true },
  { text: 'comida' },
];

const VOICE_PERMISSION_KEY = 'voiceSearchPermissionAsked';

export default function SearchBar({ whiteMode = false, onTap }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showVoiceSheet, setShowVoiceSheet] = useState(false);
  const inputRef = useRef(null);

  const voiceSupported = vozSoportada;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWordIndex((prev) => (prev + 1) % PALABRAS_ROTATIVAS.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    localStorage.setItem('searchQuery', value);
    window.dispatchEvent(new Event('searchUpdated'));
  };

  useEffect(() => {
    const handleExternal = () => {
      setSearchQuery(localStorage.getItem('searchQuery') || '');
    };
    window.addEventListener('searchUpdated', handleExternal);
    return () => window.removeEventListener('searchUpdated', handleExternal);
  }, []);

  // Notifica globalmente cuando el sheet de voz se abre/cierra, para que otros
  // overlays bottom-pinned (ej. HomeCartSummary) se oculten y no se vean
  // detrás del backdrop oscuro.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('voiceSheetToggle', { detail: { open: showVoiceSheet } }));
  }, [showVoiceSheet]);

  // Tap al mic. Si es la primera vez en este navegador, mostramos modal
  // informativo de permiso. Después, vamos directo al sheet (que internamente
  // dispara el permission prompt nativo del browser si aún no se concedió).
  const handleMicClick = (e) => {
    e.stopPropagation();
    const asked = localStorage.getItem(VOICE_PERMISSION_KEY);
    if (asked === 'true') {
      setShowVoiceSheet(true);
    } else {
      setShowPermissionModal(true);
    }
  };

  const handlePermissionAllow = () => {
    localStorage.setItem(VOICE_PERMISSION_KEY, 'true');
    setShowPermissionModal(false);
    setShowVoiceSheet(true);
  };

  const handlePermissionDeny = () => {
    setShowPermissionModal(false);
  };

  // Cuando el sheet devuelve un transcript, llenamos el input y abrimos
  // automáticamente el SearchOverlay (via onTap) para que el usuario vea
  // los resultados / sugerencias de su búsqueda dictada.
  const handleVoiceResult = (text) => {
    setSearchQuery(text);
    localStorage.setItem('searchQuery', text);
    window.dispatchEvent(new Event('searchUpdated'));
    onTap?.();
  };

  return (
    <>
      <div className="px-4 pt-2 pb-2">
        <div className="relative w-full">
          <div
            className={`${whiteMode ? 'bg-gray-100' : 'bg-white'} border border-gray-100 rounded-xl flex items-center transition-colors duration-300 ease-out`}
            onClick={onTap}
            role={onTap ? 'button' : undefined}
          >
            <PiMagnifyingGlassBold size={18} className="text-gray-700 ml-3 flex-shrink-0" />
            <input
              ref={inputRef}
              type="search"
              readOnly={!!onTap}
              aria-label="Buscar productos y negocios"
              className="w-full pl-3 pr-3 py-2.5 bg-transparent outline-none text-gray-700 text-base cursor-pointer"
              value={searchQuery}
              onChange={handleChange}
              onFocus={(e) => {
                if (onTap) {
                  e.target.blur();
                  onTap();
                }
              }}
            />
            {voiceSupported && (
              <>
                {/* Divisor entre input y mic */}
                <span className="h-5.5 w-px bg-gray-200 mr-3 flex-shrink-0" aria-hidden="true" />
                <button
                  type="button"
                  onClick={handleMicClick}
                  className="mr-3 flex-shrink-0 active:scale-90 transition-transform"
                  aria-label="Búsqueda por voz"
                >
                  <Microphone size={18} weight="fill" className="text-gray-800" />
                </button>
              </>
            )}
          </div>
          {!searchQuery && (
            <div className="absolute inset-y-0 left-0 right-12 flex items-center pl-11 pointer-events-none">
              <span className="text-gray-500 text-[14px]">Busca tu&nbsp;</span>
              <AnimatePresence mode="wait">
                <motion.span
                  key={currentWordIndex}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                  className="text-gray-500 text-[14px] inline-flex items-center"
                >
                  {PALABRAS_ROTATIVAS[currentWordIndex].text}
                  {PALABRAS_ROTATIVAS[currentWordIndex].hasFlame && (
                    <Fire size={14} weight="fill" className="ml-1 text-searchbar-flame-icon" />
                  )}
                </motion.span>
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {/* Modal informativo previo al permission prompt nativo del browser.
          Solo se muestra la primera vez; después se va directo al sheet. */}
      <ConfirmModal
        isOpen={showPermissionModal}
        onClose={handlePermissionDeny}
        onConfirm={handlePermissionAllow}
        title="Permitir acceso al micrófono"
        message="Te ayudará a traducir tu voz a una búsqueda dentro de VaChido."
        confirmLabel="Permitir"
        cancelLabel="Denegar"
      />

      {/* Sheet de búsqueda por voz — maneja sus propios estados internos
          (prompt → listening → error/success). */}
      <VoiceSearchSheet
        isOpen={showVoiceSheet}
        onClose={() => setShowVoiceSheet(false)}
        onTranscript={handleVoiceResult}
        onUsarTeclado={onTap}
      />
    </>
  );
}
